import { useEffect, useState, useMemo } from "react";
import { Rocket, CalendarClock } from "lucide-react";
import { api } from "@/lib/api";

const DAY = 86400000, HOUR = 3600000, MIN = 60000;
const PROJECT_START = "2026-01-01"; // used only for the elapsed-time progress bar

// Minutes that Europe/London is ahead of UTC at the given instant (DST-aware).
function londonOffsetMinutes(date) {
  const dtf = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/London", hour12: false,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
  const p = dtf.formatToParts(date).reduce((a, x) => ((a[x.type] = x.value), a), {});
  const asUTC = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second);
  return Math.round((asUTC - date.getTime()) / 60000);
}

// UTC ms for 23:59:00 UK local time on YYYY-MM-DD.
export function ukDeadlineMs(dateStr) {
  const [y, m, d] = dateStr.split("-").map(Number);
  const guess = Date.UTC(y, m - 1, d, 23, 59, 0);
  const offset = londonOffsetMinutes(new Date(guess));
  return guess - offset * 60000;
}

const pad = (n) => String(n).padStart(2, "0");
const fmtDate = (dateStr) => {
  try {
    return new Date(`${dateStr}T12:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  } catch { return dateStr; }
};

// tone: colour + label for each state
const STATE_TONE = {
  neutral: "text-[#67E8F9] border-[#67E8F9]/40 bg-[#67E8F9]/10",
  prep: "text-[#67E8F9] border-[#67E8F9]/40 bg-[#67E8F9]/10",
  approaching: "text-amber-200 border-amber-300/40 bg-amber-300/10",
  final: "text-orange-200 border-orange-300/40 bg-orange-300/10",
  almost: "text-rose-200 border-rose-300/40 bg-rose-300/10",
  launchday: "text-emerald-200 border-emerald-300/40 bg-emerald-300/10",
  launched: "text-emerald-200 border-emerald-300/40 bg-emerald-300/10",
};

function computeState(deadline, now) {
  const diff = deadline - now;
  if (diff <= 0) {
    const since = Math.floor((now - deadline) / DAY);
    if (since < 1) return { launched: true, key: "launchday", label: "Launch Day", subtitle: "Edju Cat launches today", days: 0, hours: 0, mins: 0, secs: 0 };
    return { launched: true, key: "launched", label: "Edju Cat launched", subtitle: `${since} day${since === 1 ? "" : "s"} since launch`, daysSince: since };
  }
  const days = Math.floor(diff / DAY);
  const hours = Math.floor((diff % DAY) / HOUR);
  const mins = Math.floor((diff % HOUR) / MIN);
  const secs = Math.floor((diff % MIN) / 1000);
  let key = "neutral", label = "On schedule";
  if (diff / HOUR < 24) { key = "almost"; label = "Launch day is almost here"; }
  else if (days > 90) { key = "neutral"; label = "On schedule"; }
  else if (days >= 31) { key = "prep"; label = "Launch preparation underway"; }
  else if (days >= 15) { key = "approaching"; label = "Launch approaching"; }
  else { key = "final"; label = "Final launch countdown"; }
  return { launched: false, key, label, days, hours, mins, secs };
}

const Block = ({ value, label, testid }) => (
  <div className="flex min-w-0 flex-1 flex-col items-center justify-center rounded-xl border border-white/10 bg-white/[0.06] px-2 py-1.5">
    <span
      data-testid={testid}
      className="font-numeric text-xl font-bold tabular-nums leading-none text-white sm:text-2xl"
    >
      {value}
    </span>
    <span className="mt-1 text-[8px] font-semibold uppercase tracking-[0.16em] text-[#AEBEE8]">{label}</span>
  </div>
);

export const LaunchCountdown = ({ variant = "command", refreshKey, readiness: readinessProp }) => {
  const [info, setInfo] = useState(null); // { date, readiness }
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    let active = true;
    api.list("launch-summary")
      .then((s) => { if (active) setInfo({ date: s.target_launch_date, readiness: s.readiness }); })
      .catch(() => {});
    return () => { active = false; };
  }, [refreshKey]);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const deadline = useMemo(() => (info?.date ? ukDeadlineMs(info.date) : null), [info?.date]);
  const state = useMemo(() => (deadline ? computeState(deadline, now) : null), [deadline, now]);
  const progress = useMemo(() => {
    if (!deadline) return 0;
    const start = Date.parse(`${PROJECT_START}T00:00:00Z`);
    return Math.max(0, Math.min(100, ((now - start) / (deadline - start)) * 100));
  }, [deadline, now]);

  if (!info || !state) return null;
  const readiness = readinessProp ?? info.readiness ?? 0;
  const tone = STATE_TONE[state.key];
  const isTracker = variant === "tracker";

  const srText = state.launched
    ? (state.key === "launchday" ? `Launch day. Edju Cat launches today, ${fmtDate(info.date)}.` : `Edju Cat launched ${state.daysSince} days ago on ${fmtDate(info.date)}.`)
    : `${state.days} days, ${state.hours} hours, ${state.mins} minutes and ${state.secs} seconds until Edju Cat launches on ${fmtDate(info.date)}. Launch readiness ${readiness} percent.`;

  return (
    <section
      data-testid={`launch-countdown-${variant}`}
      aria-label="Launch countdown"
      className={`relative overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-br from-[#16307E] via-[#14245F] to-[#0B1E5B] ${isTracker ? "p-5 sm:p-6" : "p-4 sm:p-5"}`}
    >
      <span
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-[0.5]"
        style={{ background: "radial-gradient(circle at 88% 15%, rgba(103,232,249,0.18) 0, transparent 45%)" }}
      />
      <p className="sr-only" role="timer" aria-live="off">{srText}</p>

      <div className="relative z-10">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#67E8F9]/15 text-[#67E8F9]">
              <Rocket size={15} aria-hidden="true" />
            </span>
            <div>
              <p className={`font-heading font-semibold text-white ${isTracker ? "text-lg" : "text-base"}`}>Until Edju Cat Launch</p>
              <p className="flex items-center gap-1.5 text-xs text-[#AEBEE8]">
                <CalendarClock size={12} aria-hidden="true" /> {fmtDate(info.date)} · 11:59 PM UK
              </p>
            </div>
          </div>
          <span data-testid="countdown-status" className={`rounded-full border px-3 py-1 text-xs font-semibold ${tone}`}>
            {state.label}
          </span>
        </div>

        {state.key === "launched" ? (
          <div className="flex items-end gap-3">
            <span data-testid="countdown-days-since" className="font-numeric text-5xl font-bold leading-none text-white sm:text-6xl">{state.daysSince}</span>
            <span className="mb-1 text-sm text-[#AEBEE8]">{state.subtitle}</span>
          </div>
        ) : (
          <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch">
            {/* Prominent DAYS figure */}
            <div className="flex flex-1 items-center gap-4 rounded-2xl border border-[#67E8F9]/25 bg-gradient-to-br from-[#67E8F9]/15 to-transparent px-5 py-2.5">
              <span data-testid="countdown-days" className="font-numeric text-5xl font-bold leading-none text-white sm:text-6xl">
                {state.days}
              </span>
              <div className="leading-tight">
                <p className="font-heading text-base font-semibold text-[#67E8F9]">Days</p>
                <p className="text-xs text-[#AEBEE8]">until launch</p>
              </div>
            </div>
            {/* Smaller HMS blocks */}
            <div className="grid grid-cols-3 gap-2 sm:w-[46%]">
              <Block testid="countdown-hours" value={pad(state.hours)} label="Hours" />
              <Block testid="countdown-minutes" value={pad(state.mins)} label="Minutes" />
              <Block testid="countdown-seconds" value={pad(state.secs)} label="Seconds" />
            </div>
          </div>
        )}

        <div className="mt-3.5">
          <div className="mb-1.5 flex items-center justify-between text-xs">
            <span className="font-semibold text-[#AEBEE8]">Launch readiness</span>
            <span data-testid="countdown-readiness" className="rounded-full bg-[#67E8F9]/15 px-2.5 py-0.5 font-bold text-[#67E8F9]">{readiness}% ready</span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-full bg-white/10" role="progressbar" aria-valuenow={readiness} aria-valuemin={0} aria-valuemax={100} aria-label="Launch readiness">
            <div className="h-full rounded-full bg-gradient-to-r from-[#22D3EE] to-[#67E8F9] transition-[width] duration-1000 ease-out" style={{ width: `${readiness}%` }} />
          </div>
        </div>
      </div>
    </section>
  );
};
